import React, { use, useCallback, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import ScreenCard from "../ScreenCard";
import { useI18n } from "../../hooks/i18n-hooks";
import TextField from "../../components/TextField";
import VStack from "../../ui_atoms/VStack";
import { useTheme } from "../../hooks/use-theme";
import { Address } from "../../components/address/address";
import AddressField from "../../components/address/AddressField";
import Switch from "../../ui_atoms/Switch";
import {
    AuthManagerContext,
    IS_INITIAL_WORKER_ONBOARDING,
    IS_POST_ONBOARDING_PREPARE_DISPLAYED,
} from "../../contexts/AuthManager";
import isEmail from "validator/lib/isEmail";
import Text from "../../components/Text";
import { useIntercom } from "../../hooks/intercom";
import MarkdownView from "../../components/MarkdownView";
import { SignupProfileType, useSignup } from "../../hooks/use-signup";
import { CompositeScreenProps } from "@react-navigation/core";
import Gap from "../../ui_atoms/Gap";
import OptionsField from "../../components/OptionsField";
import { BodyMediumText, LinkSmallTextBold } from "../../components/FormattedText";
import { safeAsyncStorage } from "../../utils/safe-async-storage";
import { StackScreenProps } from "@react-navigation/stack";
import { SignupSigninStackParamList } from "../../navigations/SignupSigninStack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useChainInputFocus } from "../../hooks/use-chain-input-focus";
import { usePreventGoingBack } from "../../hooks/use-prevent-going-back";
import { useAtomValue } from "jotai";
import { currentLang } from "../../atoms/i18n";

type FormData = {
    email: string;
    address: Address;
    terms: boolean;
    swissOrPermit?: boolean;
};

type CheckWorkerInfoScreenProps = CompositeScreenProps<
    StackScreenProps<RootStackParamList, "CheckWorkerInfo">,
    StackScreenProps<SignupSigninStackParamList, "CheckWorkerInfo">
>;

const CheckWorkerInfo = ({ route }: CheckWorkerInfoScreenProps) => {
    const i18n = useI18n();
    const styles = useStyles();
    const { user, appExpoPushToken } = use(AuthManagerContext);
    const { signup, loading, error } = useSignup(SignupProfileType.WORKER);
    const { invitation, invitationCode } = route.params || {};
    const lang = useAtomValue(currentLang);

    const {
        handleSubmit,
        control,
        formState: { errors, isValid: isFormValid, isDirty },
    } = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            ...(user?.workerInfo?.email ? { email: user.workerInfo.email } : {}),
            ...(user?.workerInfo?.address ? { address: user.workerInfo.address } : {}),
        },
    });
    const isValid = isFormValid;
    const { presentArticleId, loginUnidentifiedUser } = useIntercom();
    const openMarkdownLink = useCallback(
        (url: string) => {
            void (async () => {
                if (url.startsWith("intercom://")) {
                    const articleId = url.split("intercom://")[1];
                    await loginUnidentifiedUser();
                    await presentArticleId(articleId);
                }
            })();
            return false;
        },
        [loginUnidentifiedUser, presentArticleId],
    );

    const onSubmit = useCallback<SubmitHandler<FormData>>(
        (data) => {
            const { email, address, swissOrPermit } = data;

            if (isValid) {
                void safeAsyncStorage.setItem(IS_POST_ONBOARDING_PREPARE_DISPLAYED, "true");
                void safeAsyncStorage.setItem(IS_INITIAL_WORKER_ONBOARDING, "true");
                void signup({
                    swissOrHasPermitAnswer: swissOrPermit,
                    user: {
                        email,
                        address,
                        expoPushToken: appExpoPushToken,
                        lang,
                    },
                    // An invitation found by its code (sent by email) is claimed and accepted
                    // by the signup with that code: it does not belong to this user yet.
                    ...(invitationCode
                        ? { invitationCode }
                        : { invitationToken: invitation?.token }),
                });
            }
        },
        [appExpoPushToken, invitation, invitationCode, isValid, signup, lang],
    );

    usePreventGoingBack({ prevent: loading || isDirty });

    const { chainInputFocus } = useChainInputFocus();

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("submit"),
                    isValid: isValid && !loading,
                    validating: loading,
                    onPress: handleSubmit(onSubmit),
                },
            ]}
            headband={
                error
                    ? {
                          text: i18n.t("errors.unknown_error"),
                          mode: "error",
                      }
                    : undefined
            }
        >
            <VStack gap={1} h="2/3">
                <>
                    <Text style={styles.introText}>
                        {i18n.t("screens.auth.check_worker_info.intro")}
                    </Text>
                    <LinkSmallTextBold
                        onPress={() => {
                            void presentArticleId("8895605");
                        }}
                    >
                        {i18n.t("know_more")}
                    </LinkSmallTextBold>
                </>
                <Controller
                    name="address"
                    control={control}
                    rules={{
                        required: true,
                    }}
                    render={({ field: { onChange, value, name } }) => (
                        <AddressField
                            {...chainInputFocus(name)}
                            label={i18n.t("screens.auth.check_worker_info.residential_address")}
                            value={value}
                            onSelect={onChange}
                            error={!!errors.address}
                            // do not filter, we are limited to 5 countries
                            //allowedCountries={["ch", "it"]}
                            disabled={loading}
                            requiresPreciseAddress
                        />
                    )}
                />
                <Controller
                    name="email"
                    control={control}
                    rules={{
                        required: true,
                        validate: (value) =>
                            !isEmail(value) ? i18n.t("errors.field_name.email") : undefined,
                    }}
                    render={({ field: { onChange, onBlur, value, name } }) => (
                        <TextField
                            {...chainInputFocus(name)}
                            label={i18n.t("user.email")}
                            value={value}
                            onChangeText={onChange}
                            onBlur={onBlur}
                            keyboardType="email-address"
                            textContentType="emailAddress"
                            autoComplete="email"
                            autoCorrect={false}
                            autoCapitalize="none"
                            returnKeyType="next"
                            error={errors.email?.message}
                            disabled={loading}
                        />
                    )}
                />
                <Controller
                    name="swissOrPermit"
                    control={control}
                    rules={{
                        validate: (value) =>
                            value !== undefined ? true : i18n.t("errors.field_name.swissOrPermit"),
                    }}
                    render={({ field: { onChange, value } }) => (
                        <>
                            <BodyMediumText>
                                {i18n.t("screens.auth.check_worker_info.has_permit")}
                            </BodyMediumText>
                            <OptionsField<boolean | undefined>
                                data={[
                                    { label: i18n.t("yes"), value: true },
                                    { label: i18n.t("no"), value: false },
                                ]}
                                onSelect={onChange}
                                value={value}
                                disabled={loading}
                                shrinked
                            />
                        </>
                    )}
                />
                <Controller
                    name="terms"
                    control={control}
                    rules={{
                        required: true,
                    }}
                    render={({ field: { onChange, value } }) => (
                        <View style={styles.termsContainer}>
                            <Switch
                                testID="switch-accept-terms"
                                value={value}
                                onChange={onChange}
                                disabled={loading}
                            />
                            <Gap gap="1/2" />
                            <MarkdownView
                                style={styles.termsMarkdown}
                                markdown={i18n.t(
                                    "screens.auth.check_worker_info.accept_terms_of_use",
                                )}
                                onLinkPress={openMarkdownLink}
                            />
                        </View>
                    )}
                />
            </VStack>
        </ScreenCard>
    );
};

const useStyles = () => {
    const { fonts, gridSize } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                introText: {
                    ...fonts.bodyMedium,
                    marginTop: gridSize,
                },
                termsContainer: {
                    flexDirection: "row",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                },
                termsText: {
                    ...fonts.bodyMedium,
                    flexShrink: 1,
                },
                termsMarkdown: {
                    /* compensate paragraph margin of markdown */
                    marginTop: -gridSize / 4,
                    flexShrink: 1,
                },
            }),
        [fonts.bodyMedium, gridSize],
    );
};

export default CheckWorkerInfo;
